import { LoadingBlur } from '@ics-portal/guardian';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { InfoCardTemplate } from '@components/_templates/InfoCardTemplate';
import ContentController from '@components/_organisms/Headers/ContentController';
import TitleWithHelpLink from '@components/_molecules/TitleWithHelpLink';
import {
    ProjectSummaryProvider,
    useProjectSummary,
} from '../_utils/contexts/ProjectSummaryContext';
import { SummaryCardsProvider } from './_utils/contexts/SummaryCardsContext';
import s from './Summary.module.scss';
import { ProjectInfo } from './_utils/components/ProjectInfo';
import AssetsCard from './_utils/components/cards/AssetsCard';
import ComponentsCard from './_utils/components/cards/ComponentsCard';
import { ProjectMilestonesCard } from './_utils/components/cards/ProjectMilestonesCard';
import { PsspClassificationCard } from './_utils/components/cards/PsspClassificationCard';
import SecurityZonesCard from './_utils/components/cards/SecurityZonesCard';
import { TraCard } from './_utils/components/cards/TraCard';
import { VulnerabilityManagementCard } from './_utils/components/cards/VulnerabilityManagementCard';

function SummaryContent(): ReactNode {
    const { pid } = useParams<{ pid: string }>() as { pid: string };
    const { project, loading } = useProjectSummary();

    if (!project) {
        return (
            <LoadingBlur contrast loading={loading}>
                <div className="bg-se70 w-full h-full" />
            </LoadingBlur>
        );
    }

    const contentClasses = clsx('p-content content-area', s.content);

    return (
        <LoadingBlur contrast loading={loading}>
            <InfoCardTemplate>
                <ContentController
                    className={s.summary}
                    title={
                        <TitleWithHelpLink
                            label={project.name}
                            link="https://wiki.siemens-energy.com/x/Coj3FQ"
                        />
                    }
                />
                <div className={contentClasses}>
                    <div
                        className="gap-4"
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'minmax(0, 3fr) minmax(0, 1fr)',
                        }}
                    >
                        <div className="flex flex-column gap-4">
                            <div className="flex gap-4">
                                <ProjectMilestonesCard pid={pid} />
                                <PsspClassificationCard pid={pid} />
                                <TraCard pid={pid} />
                            </div>
                            <div className="flex gap-4">
                                <AssetsCard pid={pid} />
                                <ComponentsCard pid={pid} />
                                <SecurityZonesCard pid={pid} />
                            </div>
                            <VulnerabilityManagementCard />
                        </div>
                        <aside>
                            <ProjectInfo />
                        </aside>
                    </div>
                </div>
            </InfoCardTemplate>
        </LoadingBlur>
    );
}

export default function Summary(): ReactNode {
    return (
        <ProjectSummaryProvider>
            <SummaryCardsProvider>
                <SummaryContent />
            </SummaryCardsProvider>
        </ProjectSummaryProvider>
    );
}
